// src/utils/constants.ts

// 1. MAPS BUTTON LABELS TO IDs
export const STORE_MAP: Record<string, string> = {
  'All Stores': 'all',
  'Amazon': 'amazon',
  'Tesstudio': 'tesstudio',
  'Hanshow': 'hanshow',
  'EVBase': 'evbase',
  'Tesery': 'tesery',
  'AliExpress': 'aliexpress'
};

export const STORE_LOGOS: Record<string, any> = {
  'Amazon': '/logos/amazon.svg',
  'Tesstudio': '/logos/tesstudio.avif', // You can add these image files to public later
  'Hanshow': { src: '/logos/hanshow.png', scale: 1.4 },
  'EVBase': { src: '/logos/evbase.png', scale: 1.4 },
  'Tesery': '/logos/tesery.png',
  'AliExpress': { src: '/logos/aliexpress.svg', scale: 2.6 }
};

export const STORE_VARIANTS: Record<string, string[]> = {
  'amazon': ['amazon', 'Amazon', 'AMAZON'],
  'tesstudio': ['tesstudio', 'Tesstudio', 'TESSTUDIO'],
  'hanshow': ['hanshow', 'Hanshow', 'HANSHOW'],
  'evbase': ['evbase', 'EVBase', 'EVBASE', 'ev base'],
  'tesery': ['tesery', 'Tesery', 'TESERY'],
  'aliexpress': ['aliexpress', 'AliExpress', 'ALIEXPRESS', 'ali express']
};

export const DEAL_TYPES = [
  'All Types', 
  'Glitch', 
  'Daily Deal', 
  'Price Drop',       // New: For the audit bot
  'BOGO & Free Gift', // Merged: Clean sidebar
  'Buy More Save More', // Explicit: What you asked for
  'Clearance', 
  'Sale'
];
export const BRANDS = ['All Brands', 'Tesla', 'Spigen', 'Tesmanian', 'Abstract Ocean', 'Michelin'];


export interface Deal {
  id?: string; 
  title: string;
  price: number;
  originalPrice: number;
  url: string;
  image: string | null;
  store: string;
  brand?: string;
  vehicle?: string;
  category: string;
  description?: string;
  dealType?: string;
  promoType?: string;
  status?: string; // 'active', 'archived', 'hidden'
  inStock?: boolean;
  hot?: boolean;
  curatorNote?: string;
  timestamp: any; // Accommodates both numbers and Firebase serverTimestamps
  manualLock?: boolean;
  isVerified?: boolean;
  click_count?: number;
  priceHistory?: { ts: number; price: number }[];
  is30DayLow?: boolean;
  hiddenAt?: number;
  hiddenReason?: string;
}


// Fixed: The actual color mappings
export const STORE_COLORS: { [key: string]: string } = {
  'amazon': 'bg-slate-800 text-white border-slate-700',
  'tesstudio': 'bg-blue-600 text-white border-blue-500',
  'hanshow': 'bg-black text-white border-zinc-800',
  'evbase': 'bg-teal-600 text-white border-teal-500',
  'tesery': 'bg-indigo-600 text-white border-indigo-500',
  'aliexpress': 'bg-orange-600 text-white border-orange-500',
};

// Helper function to format time
export function getTimeAgo(timestamp: number) {
  if (!timestamp) return '';
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ⚠️ ORIGINAL AFFILIATE LOGIC PRESERVED ⚠️
export function getHighCommissionLink(url: string | undefined) {
    if (!url) return '';
    
    // 1. UNWRAP: If it's a "bad" link (sjv.io, gvw9.net, etc), extract the real product URL
    let targetUrl = url;
    if (url.includes('sjv.io') || url.includes('gvw9.net') || url.includes('impact.com')) {
        try {
            const urlObj = new URL(url);
            const nestedUrl = urlObj.searchParams.get('u') || urlObj.searchParams.get('url');
            if (nestedUrl) targetUrl = decodeURIComponent(nestedUrl);
        } catch (e) { console.error("Error parsing affiliate link", e); }
    }

    // 2. ACE HARDWARE FIX (The "dttq.net" Wrapper)
    if (targetUrl.includes('acehardware.com') && !targetUrl.includes('dttq.net')) {
         const ACE_MPID = "1885322";
         const ACE_AD_ID = "1171219"; 
         const ACE_CAMPAIGN = "9988";
         const encoded = encodeURIComponent(targetUrl);
         return `https://acehardware.dttq.net/c/${ACE_MPID}/${ACE_AD_ID}/${ACE_CAMPAIGN}?u=${encoded}`;
    }

    // 3. TRUST: If it's already a good creator link, don't touch it
    if (targetUrl.includes('thehomedepotcreator.pxf.io') || targetUrl.includes('dttq.net')) return targetUrl;

    // 4. HOME DEPOT WRAPPER
    if (targetUrl.includes('homedepot.com') && !targetUrl.includes('pxf.io')) {
        const HD_CREATOR_DOMAIN = "thehomedepotcreator.pxf.io";
        const MY_MPID = "1885322"; 
        const HD_AD_ID = "2346355"; 
        const HD_CAMPAIGN_ID = "28226";
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://${HD_CREATOR_DOMAIN}/c/${MY_MPID}/${HD_AD_ID}/${HD_CAMPAIGN_ID}?u=${encodedTarget}`;
    }

    // 5. OHIO POWER TOOL WRAPPER
    if (targetUrl.toLowerCase().includes('ohiopowertool.com') && !targetUrl.includes('awin1.com')) {
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://www.awin1.com/cread.php?awinmid=89545&awinaffid=2052477&ued=${encodedTarget}`;
    }

    // 6. AWIN NETWORK WRAPPERS (Olight, Dovoh, EcoFlow, Jackery)
    if ((targetUrl.toLowerCase().includes('olightstore.com') || targetUrl.toLowerCase().includes('olight.com')) && !targetUrl.includes('awin1.com')) {
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://www.awin1.com/cread.php?awinmid=90861&awinaffid=2052477&ued=${encodedTarget}`;
    }
    if (targetUrl.toLowerCase().includes('dovoh.com') && !targetUrl.includes('awin1.com')) {
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://www.awin1.com/cread.php?awinmid=57741&awinaffid=2052477&ued=${encodedTarget}`;
    }
    if (targetUrl.toLowerCase().includes('ecoflow.com') && !targetUrl.includes('awin1.com')) {
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://www.awin1.com/cread.php?awinmid=59181&awinaffid=2052477&ued=${encodedTarget}`;
    }
    if (targetUrl.toLowerCase().includes('jackery.com') && !targetUrl.includes('awin1.com')) {
        const encodedTarget = encodeURIComponent(targetUrl);
        return `https://www.awin1.com/cread.php?awinmid=59183&awinaffid=2052477&ued=${encodedTarget}`;
    }

    // 7. EBAY CREATOR NETWORK WRAPPER
    if (targetUrl.toLowerCase().includes('ebay.com')) {
        // This regex perfectly captures the Item ID, whether it's /itm/12345 or /itm/product-name/12345
        const match = targetUrl.match(/\/itm\/(?:[^\/]+\/)?(\d+)/);
        if (match && match[1]) {
            const itemId = match[1];
            return `https://www.ebay.com/itm/${itemId}?mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5338741507`;
        }
    }
    // Always return the targetUrl as a fallback if no wrappers match
    return targetUrl;
}